import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useShell } from '@/contexts/shell-context';
import { HelpPlayerPanel } from './HelpPlayerPanel';
import { HelpTile } from './HelpTile';
import { publishedFirst, tasksThenClips } from './help-gallery';
import { isFeatureClip, type HelpEntry } from './help-types';
import { useHelpEntries, useHelpForPath } from './use-help-entries';
import { useHelpPlayer } from './use-help-player';
import { useWatchedHelp } from './use-watched-help';

/**
 * In-context Help: the Help for the screen you are on, opened from the header Help button.
 * Task videos and Flows as tiles, the best match first and full width, then the screen's
 * feature clips under "On this screen"; published videos before "Coming soon" ones. A tile
 * plays in a player pinned at the top of the sheet (Q48), with what the video does and how
 * to do it under it. Right side on desktop, bottom sheet on phones.
 */
export function HelpSheet() {
  const { helpOpen, closeHelp, isMobile } = useShell();
  const { pathname } = useLocation();
  const all = useHelpEntries();
  const entries = tasksThenClips(useHelpForPath(pathname));
  const videos = publishedFirst(entries.filter((entry) => !isFeatureClip(entry)));
  const features = publishedFirst(entries.filter(isFeatureClip));
  const { watched, markWatched } = useWatchedHelp();
  const player = useHelpPlayer(all);
  const playHere = (entry: HelpEntry) => player.play(entry, [...videos, ...features]);
  const contentRef = useRef<HTMLDivElement>(null);

  const close = () => {
    player.close();
    closeHelp();
  };

  return (
    <Sheet open={helpOpen} onOpenChange={(open) => !open && close()}>
      <SheetContent
        ref={contentRef}
        // Focus the sheet itself, not its first tile, so nothing looks chosen on open.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus();
        }}
        side={isMobile ? 'bottom' : 'right'}
        className={
          isMobile
            ? 'flex h-[85dvh] flex-col gap-0 rounded-t-2xl p-0 sm:max-w-full'
            : 'flex w-full flex-col gap-0 p-0 sm:max-w-[520px]'
        }
      >
        <SheetHeader className="border-b border-border px-4 py-3 text-left">
          <SheetTitle>Help for this screen</SheetTitle>
          <SheetDescription className="sr-only">Help videos for the screen you are on</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {player.current && (
            <div className="relative rounded-xl border border-border bg-card p-3">
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-1 top-1 z-10 h-8 w-8"
                onClick={player.close}
                aria-label="Close player"
              >
                <X className="h-4 w-4" />
              </Button>
              <HelpPlayerPanel
                entry={player.current}
                queue={player.queue}
                watched={watched}
                onWatched={markWatched}
                onAdvance={player.advance}
                onPlayQueued={player.playQueued}
              />
            </div>
          )}
          {videos.length > 0 && (
            <section aria-label="Videos" className="grid grid-cols-2 gap-2">
              {videos.map((entry, index) => {
                // The best match for this screen leads, full width, until a video is playing.
                const lead = index === 0 && !player.current;
                return (
                  <HelpTile
                    key={entry.id}
                    entry={entry}
                    watched={watched.has(entry.id)}
                    onPlay={playHere}
                    active={player.current?.id === entry.id}
                    showPurpose={lead}
                    className={lead ? 'col-span-2' : undefined}
                  />
                );
              })}
            </section>
          )}
          {features.length > 0 && (
            <section aria-labelledby="help-sheet-features" className="space-y-2">
              <h2 id="help-sheet-features" className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                On this screen
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {features.map((entry) => (
                  <HelpTile
                    key={entry.id}
                    entry={entry}
                    watched={watched.has(entry.id)}
                    onPlay={playHere}
                    active={player.current?.id === entry.id}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
        <div className="border-t border-border px-4 py-3">
          <Link to="/help" className="text-sm font-semibold text-primary underline-offset-2 hover:underline" onClick={close}>
            All help videos
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
